import React, { createContext, useContext, useEffect, useState } from 'react';

import { getBrandPrimaryHsl, subscribeBrandPrimary } from '@/services/brandingService';
import { applyBrandPalette } from '@/lib/brandPalette';
import { COLOR_HSL, DENSITY_SPACING, FONT_SIZE_PX, FONT_STACK, RADIUS_CSS, fontSizeVars } from '@/lib/appearance';

interface ThemeSettings {
  theme: 'light' | 'dark' | 'auto';
  fontSize: 'small' | 'medium' | 'large' | 'extra-large';
  fontFamily: 'inter' | 'roboto' | 'open-sans' | 'lato' | 'poppins' | 'system';
  primaryColor: 'blue' | 'purple' | 'green' | 'orange' | 'red' | 'teal' | 'pink' | 'indigo';
  accentColor: 'blue' | 'purple' | 'green' | 'orange' | 'red' | 'teal' | 'pink' | 'indigo';
  borderRadius: 'none' | 'small' | 'medium' | 'large' | 'full';
  density: 'compact' | 'comfortable' | 'spacious';
  animations: boolean;
  reducedMotion: boolean;
  highContrast: boolean;
  colorBlindMode: 'none' | 'deuteranopia' | 'protanopia' | 'tritanopia';
}

interface ThemeContextType {
  settings: ThemeSettings;
  /** True while an institute or tenant brand colour is in force (Appearance swatches then edit that colour). */
  brandColourActive: boolean;
  updateTheme: (key: keyof ThemeSettings, value: any) => void;
  resetTheme: () => void;
}

const defaultTheme: ThemeSettings = {
  theme: 'auto',
  fontSize: 'medium',
  fontFamily: 'inter',
  primaryColor: 'blue',
  accentColor: 'purple',
  borderRadius: 'medium',
  density: 'comfortable',
  animations: true,
  reducedMotion: false,
  highContrast: false,
  colorBlindMode: 'none',
};

const ThemeContext = createContext<ThemeContextType | undefined>(undefined);

export const useTheme = () => {
  const context = useContext(ThemeContext);
  if (context === undefined) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
};

interface ThemeProviderProps {
  children: React.ReactNode;
}

export const ThemeProvider: React.FC<ThemeProviderProps> = ({ children }) => {
  const [settings, setSettings] = useState<ThemeSettings>(() => {
    // Load from localStorage or use defaults
    const saved = localStorage.getItem('themeSettings');
    if (saved) {
      try {
        return { ...defaultTheme, ...JSON.parse(saved) };
      } catch (error) {
        console.error('Error parsing theme settings:', error);
      }
    }
    return defaultTheme;
  });

  // Bumped when the tenant or institute brand colour changes, so the palette
  // below repaints without a settings change.
  const [brandTick, setBrandTick] = useState(0);
  useEffect(() => subscribeBrandPrimary(() => setBrandTick((n) => n + 1)), []);

  // Save to localStorage whenever settings change
  useEffect(() => {
    localStorage.setItem('themeSettings', JSON.stringify(settings));
  }, [settings]);

  // Apply theme to document
  useEffect(() => {
    const root = document.documentElement;

    // Theme (light/dark/auto)
    let actualTheme = settings.theme;
    if (settings.theme === 'auto') {
      actualTheme = window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
    }

    root.classList.remove('light', 'dark');
    root.classList.add(actualTheme);

    // Font family: full fallback chains (lib/appearance FONT_OPTIONS), so the
    // page never renders in the browser's default sans while the webfont loads.
    root.style.setProperty('--font-family', FONT_STACK[settings.fontFamily] ?? FONT_STACK.inter);

    // Font size: the base and every size derived from it (.text-xs ... .text-3xl).
    Object.entries(fontSizeVars(FONT_SIZE_PX.dashboard[settings.fontSize] ?? 14)).forEach(([key, value]) => {
      root.style.setProperty(key, value);
    });

    // Primary color
    const primaryColorMap = COLOR_HSL;
    // A white-label tenant's own colour outranks the palette map, for both the
    // primary and the accent — an institute picks one brand colour, not two.
    //
    // Read on every run rather than once, so changing a theme setting after the
    // branding lookup has landed cannot silently restore the platform blue.
    // BrandingContext is the other writer and follows the same rule.
    const brandPrimary = getBrandPrimaryHsl();
    const primary = brandPrimary || primaryColorMap[settings.primaryColor];
    const accent = brandPrimary || primaryColorMap[settings.accentColor];

    // --primary, --accent, --gradient-primary and the blue/indigo shade
    // scales every hard-coded blue class reads (lib/brandPalette.ts). The
    // untouched default (no brand, Blue preset) keeps Tailwind's own blues.
    applyBrandPalette(root, primary, accent, Boolean(brandPrimary) || settings.primaryColor !== 'blue');

    // Border radius
    root.style.setProperty('--radius', RADIUS_CSS[settings.borderRadius] ?? RADIUS_CSS.medium);

    // Density (spacing)
    Object.entries(DENSITY_SPACING[settings.density] ?? DENSITY_SPACING.comfortable).forEach(([key, value]) => {
      root.style.setProperty(key, value);
    });

    // Animations and motion
    root.style.setProperty(
      '--animation-duration',
      settings.animations && !settings.reducedMotion ? '200ms' : '0ms',
    );
    root.classList.toggle('reduced-motion', settings.reducedMotion);

    // High contrast
    root.classList.toggle('high-contrast', settings.highContrast);

    // Color blind mode
    root.classList.remove('deuteranopia', 'protanopia', 'tritanopia');
    if (settings.colorBlindMode !== 'none') {
      root.classList.add(settings.colorBlindMode);
    }
  }, [settings, brandTick]);

  // Listen for system theme changes when in auto mode
  useEffect(() => {
    if (settings.theme === 'auto') {
      const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
      const handleChange = () => {
        const root = document.documentElement;
        root.classList.remove('light', 'dark');
        root.classList.add(mediaQuery.matches ? 'dark' : 'light');
      };

      mediaQuery.addEventListener('change', handleChange);
      return () => mediaQuery.removeEventListener('change', handleChange);
    }
  }, [settings.theme]);

  const updateTheme = (key: keyof ThemeSettings, value: any) => {
    setSettings((prev) => ({
      ...prev,
      [key]: value,
    }));
  };

  const resetTheme = () => {
    setSettings(defaultTheme);
    localStorage.removeItem('themeSettings');
  };

  const value = {
    settings,
    brandColourActive: Boolean(getBrandPrimaryHsl()),
    updateTheme,
    resetTheme,
  };

  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
};

export default ThemeProvider;
